import axios from 'axios';

import { message } from 'antd';
import authService from './login/AuthService';

// 使用 antd 的 message 组件
const showMessage = (text, type = 'error') => {
  if (type === 'error') {
    message.error(text);
  } else {
    message.success(text);
  }
};

const handleAuthError = (text) => {
  showMessage(text || '登录已过期，请重新登录', 'error');
  authService.logout();
};

// 创建通用请求拦截器
const createRequestInterceptor = () => {
  return (config) => {
    // 从localStorage获取token
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  };
};

// 创建通用响应拦截器
const createResponseInterceptor = () => {
  return [
    (response) => {
      const res = response.data;
      // 兼容不同的响应格式：
      // - 业务统一封装：{ res_status_code: '0', res_content: ... }
      // - 原始返回：string / null / array / object without res_status_code
      if (res === null || res === undefined) return res;
      if (typeof res !== 'object') return res;

      const responseCode =
        res.res_status_code ??
        res.resStatusCode ??
        res.code ??
        res.status_code ??
        res.statusCode;

      if (responseCode === undefined || responseCode === null) {
        // 未采用统一业务封装时，按成功返回，避免误报“网络错误”
        return res;
      }

      if (String(responseCode) === '0') {
        return res;
      }

      const responseMessage = res.res_message ?? res.message ?? res.msg;
      switch (String(responseCode)) {
          case '401':
            // 未授权，清除token并跳转到登录页
            handleAuthError(responseMessage);
            break;
          case '403':
            handleAuthError('没有权限访问该资源');
            break;
          case '2000':
            // 未授权，清除token并跳转到登录页
            handleAuthError(responseMessage);
            break;
          case '404':
            showMessage('请求的资源不存在', 'error');
            break;
          case '500':
            showMessage('服务器内部错误', 'error');
            break;
          default:
            showMessage(responseMessage || '网络错误，请稍后重试', 'error');
        }

      return Promise.reject(new Error(responseMessage || 'request_failed'));
    },
    (error) => {
      // 处理HTTP错误状态码
      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 401:
            // 未授权，清除token并跳转到登录页
            handleAuthError(data?.message);
            break;
          case 403:
            handleAuthError('没有权限访问该资源');
            break;
          case 2000:
            // 未授权，清除token并跳转到登录页
            handleAuthError(data?.message);
            break;
          case 404:
            showMessage('请求的资源不存在', 'error');
            break;
          case 500:
            showMessage('服务器内部错误', 'error');
            break;
          default:
            showMessage(data?.message || '网络错误，请稍后重试', 'error');
        }
      } else if (error.request) {
        showMessage('网络连接失败，请检查网络', 'error');
      } else {
        showMessage('请求配置错误', 'error');
      }

      return Promise.reject(error);
    }
  ];
};

// 创建业务接口请求实例（使用 VITE_API_BASE_URL）
const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api', // 从环境变量读取API基础路径
  timeout: 10000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
});

// 创建认证接口请求实例（使用 VITE_APP_OIDC_AUTHORITY）
const authRequest = axios.create({
  baseURL: import.meta.env.VITE_APP_OIDC_AUTHORITY || '/api', // 从环境变量读取OIDC认证服务器地址
  timeout: 10000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
});

// 为两个实例添加相同的拦截器
request.interceptors.request.use(createRequestInterceptor(), (error) => {
  return Promise.reject(error);
});

request.interceptors.response.use(...createResponseInterceptor());

authRequest.interceptors.request.use(createRequestInterceptor(), (error) => {
  return Promise.reject(error);
});

authRequest.interceptors.response.use(...createResponseInterceptor());


export { request, authRequest };